import {useBoardFocusWindowTarget} from '@github-ui/board-core'
import {useEffect} from 'react'

import type {BoardAxisWindow} from './use-board-window'

export type BoardWindowFocusProps = {
  columns: BoardAxisWindow
  rows: BoardAxisWindow
}

/**
 * Moves the board's two windows to the position focus is owed to, when they left it out.
 *
 * The card window inside a cell moves itself, because it has the cell's own scroll
 * container and the index to move it to. A column or a row is different in kind. The cell
 * is not in the page at all, so there is no card to draw and nothing to focus, and the
 * request would stay outstanding for as long as the reader left the board where it was.
 *
 * So the window moves first and focus follows: this scrolls the axis, the column renders,
 * the cell inside it renders, that cell's own window draws the card the position names,
 * and the request that was outstanding all along lands on it. Three windows, in that
 * order, for one position.
 *
 * Only when the axis really did leave the index out. An axis that has drawn it is either
 * showing the card already or about to be scrolled by the browser as it takes focus, and a
 * second scroll of the same box would fight the first.
 *
 * **Renders nothing, deliberately.** It subscribes to a value that changes on every move,
 * which anything that drew something would pay for on every arrow key.
 */
export function BoardWindowFocus({columns, rows}: BoardWindowFocusProps) {
  const {columnIndex, rowIndex, isPending} = useBoardFocusWindowTarget()

  const scrollColumnToIndex = columns.scrollToIndex
  const scrollRowToIndex = rows.scrollToIndex
  const isColumnDrawn = columns.indexes.includes(columnIndex)
  const isRowDrawn = rows.indexes.includes(rowIndex)

  // Facts about the windows rather than the windows themselves, because a window is a
  // fresh object on every frame, and an effect depending on one would scroll the board
  // again on every frame of the scroll it started -- which is both a fight with the
  // browser and enough layout churn for a resize observer to give up mid-delivery.
  //
  // Whether an axis drew the index is a boolean, and where each window begins is a number
  // that moves only when the window does. Between them the effect runs when the answer
  // changed and at no other time, and the second pair is what gives it another go if one
  // scroll did not arrive.
  const firstDrawnColumn = columns.indexes[0]
  const firstDrawnRow = rows.indexes[0]

  useEffect(() => {
    if (!isPending) return

    /*
      An effect calling a prop rather than an event handler doing it, and deliberately: the
      thing that happened is the focus store settling on a position nothing has taken focus
      at, which is a subscription rather than an event. There is no handler to move this
      into -- the reader may have pressed nothing at all, and the same move arrives from
      `focusPosition` and from new data repairing the position.
    */
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- the trigger is a store subscription, not an event, so there is no handler to move this into.
    if (!isColumnDrawn) scrollColumnToIndex(columnIndex)
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- as above, for the other axis.
    if (!isRowDrawn) scrollRowToIndex(rowIndex)
  }, [
    isPending,
    columnIndex,
    rowIndex,
    isColumnDrawn,
    isRowDrawn,
    firstDrawnColumn,
    firstDrawnRow,
    scrollColumnToIndex,
    scrollRowToIndex,
  ])

  return null
}
